import { useEffect, useMemo, useRef } from "react";

import Lottie from "lottie-react";

import { colors as tailwindColors } from "@/lib/colors";
import { cn } from "@/lib/utils";
import animationData from "./lottie/ReboltLoader.json";

import type { LottieRef } from "lottie-react";

interface ReboltLoaderProps {
  className?: string;
  color?: string;
  speed?: number;
}

/**
 * Resolves a Tailwind color string (e.g., "rebolt-500" or "primary") to a hex color
 */
function resolveTailwindColor(colorString: string): string {
  // Handle simple color names like "primary", "white", "black"
  if (colorString in tailwindColors) {
    const color = tailwindColors[colorString as keyof typeof tailwindColors];
    if (typeof color === "string") {
      return color;
    }
  }

  // Handle nested colors like "rebolt-500", "mint-400"
  const parts = colorString.split("-");
  if (parts.length === 2) {
    const [colorName, shade] = parts;
    const colorGroup = tailwindColors[colorName as keyof typeof tailwindColors];
    if (colorGroup && typeof colorGroup === "object" && shade in colorGroup) {
      return (colorGroup as Record<string, string>)[shade];
    }
  }

  throw new Error(`Invalid Tailwind color: ${colorString}`);
}

/**
 * Converts a color string (hex or Tailwind color) to RGB array [r, g, b] normalized to 0-1 range
 */
function colorToRgbNormalized(color: string): [number, number, number] {
  // Try to resolve as Tailwind color first, fall back to hex
  let hexColor: string;
  try {
    hexColor = resolveTailwindColor(color);
  } catch {
    // If it's not a Tailwind color, assume it's a hex color
    hexColor = color;
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
  if (!result) {
    throw new Error(`Invalid color: ${color}`);
  }
  return [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255];
}

/**
 * Recursively updates color values in Lottie animation data
 */
function updateAnimationColors(data: unknown, newColor: [number, number, number]): unknown {
  if (Array.isArray(data)) {
    return data.map((item) => updateAnimationColors(item, newColor));
  }

  if (data && typeof data === "object" && data !== null) {
    const updated = { ...(data as Record<string, unknown>) };

    // Update color values in shape fills (ty: "fl" with "c" property)
    if (
      updated.ty === "fl" &&
      updated.c &&
      typeof updated.c === "object" &&
      updated.c !== null &&
      "k" in updated.c &&
      Array.isArray((updated.c as { k: unknown }).k)
    ) {
      updated.c = {
        ...(updated.c as Record<string, unknown>),
        k: newColor,
      };
    }

    // Recursively process all properties
    for (const key in updated) {
      if (Object.prototype.hasOwnProperty.call(updated, key)) {
        updated[key] = updateAnimationColors(updated[key], newColor);
      }
    }

    return updated;
  }

  return data;
}

/**
 * ReboltLoader - Animated Lottie loader component with customizable colors
 *
 * This component renders a looping Lottie animation that can be customized with different colors.
 * The color prop supports both Tailwind color strings (from your theme) and hex color codes.
 *
 * @param props - Component props
 * @param props.className - Optional CSS classes to apply to the container div. Defaults to "w-full h-full".
 * @param props.color - Optional color string to customize the animation. Supports:
 *   - Tailwind color strings with dash notation (e.g., "rebolt-500", "mint-400", "honey-600")
 *   - Simple Tailwind color names (e.g., "primary", "white", "black")
 *   - Hex color codes (e.g., "#FF5700", "FF5700")
 *   - If not provided, uses the original animation color
 * @param props.speed - Optional animation speed multiplier. Defaults to 1 (normal speed).
 *   - Values > 1 make the animation faster (e.g., 2 = 2x speed)
 *   - Values < 1 make the animation slower (e.g., 0.5 = half speed)
 *   - Must be a positive number
 *
 * @example
 * // Using Tailwind colors
 * <ReboltLoader className="w-20 h-20" color="rebolt-500" />
 * <ReboltLoader className="w-20 h-20" color="mint-400" />
 *
 * // Using simple color names
 * <ReboltLoader color="primary" />
 * <ReboltLoader color="white" />
 *
 * // Using hex colors
 * <ReboltLoader color="#FF5733" />
 *
 * // Custom speed
 * <ReboltLoader speed={2} /> // 2x faster
 * <ReboltLoader speed={0.5} /> // Half speed
 * <ReboltLoader color="rebolt-500" speed={1.5} /> // Custom color and speed
 *
 * // Default (original color, normal speed)
 * <ReboltLoader className="w-16 h-16" />
 *
 * @remarks
 * - The component imports colors from `@/lib/colors` (shared with Tailwind CSS theme)
 * - Colors are applied by recursively modifying the Lottie animation data structure
 * - Invalid colors will fall back to the original animation color with a console warning
 * - The animation loops continuously by default
 */
export default function ReboltLoader({ className, color, speed = 1 }: ReboltLoaderProps) {
  const lottieRef = useRef<LottieRef["current"]>(null);

  const modifiedAnimationData = useMemo(() => {
    if (!color) {
      return animationData;
    }

    try {
      const rgbColor = colorToRgbNormalized(color);
      return updateAnimationColors(JSON.parse(JSON.stringify(animationData)), rgbColor);
    } catch (error) {
      console.warn("Failed to update animation color:", error);
      return animationData;
    }
  }, [color]);

  useEffect(() => {
    if (lottieRef.current?.setSpeed) {
      lottieRef.current.setSpeed(speed);
    }
  }, [speed]);

  return (
    <div className={cn("w-full h-full", className)}>
      <Lottie
        lottieRef={lottieRef}
        animationData={modifiedAnimationData}
        loop={true}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}
